import { useMemo } from "react"
import type {
  ProposalBatch,
  ProposalBatchStatus,
  ProposalSourceChannelType,
} from "@stockline/shared/types/ai/proposal"
import type { PreOrderEmail } from "@stockline/shared/types/orders/pre-order-email"
import { PROPOSAL_BATCH_TABLE } from "../../../lib/offline/resources/ai/proposal-batch/db"
import { PRE_ORDER_EMAIL_TABLE } from "../../../lib/offline/resources/orders/pre-order-email/db"
import { useAISync } from "../providers/AISyncProvider"
import { filterBatchesByPreOrderIds } from "./proposalBatchJunctionFilter"

export interface UseProposalBatchesByConversationOptions {
  status?: ProposalBatchStatus
}

/**
 * Pending AI-proposal batches linked to the given conversation. Mirrors the
 * `pre_order_messages` per-participant lookup for shared pre-orders: the V2
 * API records every dedup-group participant's mirror chat row id on
 * `participantChatIds`, so each member sees the badge through their own
 * chat row even though `sourceChatId` only points at the dedup leader's row.
 *
 * We run three reactive queries and merge:
 *   - by `participantChatIds` (multi-entry index) — covers group members
 *   - by `(sourceChannelType, sourceChatId)` — covers non-grouped chats and
 *     legacy batches created before participant fan-out shipped
 *   - by `pre_order_emails` junction, email-only — covers dedup Scenario 2
 *     (non-owner-only recipient) where the server-side message-id lookup
 *     misses under the group-owner user_id scope and leaves `sourceChatId`
 *     / `participantChatIds` NULL on the batch row. The junction is
 *     per-recipient by construction (fan-out writes one row per
 *     participant) so the viewer's email row is always present.
 */
export function useProposalBatchesByConversation(
  channelType: ProposalSourceChannelType | null | undefined,
  chatId: string | null | undefined,
  options: UseProposalBatchesByConversationOptions = {}
): ProposalBatch[] {
  const { reactive } = useAISync()
  const enabled = Boolean(channelType && chatId)

  // Multi-entry index on `participantChatIds`. Sent as the single condition
  // so the reactive adapter uses `where().equals()` (Dexie multi-entry path)
  // rather than full-table scan with strict equality on the array.
  const byParticipant = reactive.useQuery<ProposalBatch>(PROPOSAL_BATCH_TABLE, {
    participantChatIds: enabled ? chatId : "__none__",
  })

  // Legacy / non-grouped fallback — strict (channel, sourceChatId) match.
  const byLegacySource = reactive.useQuery<ProposalBatch>(
    PROPOSAL_BATCH_TABLE,
    {
      sourceChannelType: enabled ? channelType : "__none__",
      sourceChatId: enabled ? chatId : "__none__",
    }
  )

  // Email-only junction fallback. Walks `pre_order_emails[emailId=chatId]`
  // to find the pre-orders this email belongs to, then matches batches
  // whose `proposals[].entityId` points at any of those PO ids. The
  // `__none__` sentinel disables the junction read for non-email channels
  // and when the hook is disabled.
  const emailEnabled = enabled && channelType === "email"
  const emailJunctionRows = reactive.useQuery<PreOrderEmail>(
    PRE_ORDER_EMAIL_TABLE,
    { emailId: emailEnabled ? chatId : "__none__" }
  )
  const preOrderIdsForEmail = useMemo(() => {
    if (!emailEnabled) return new Set<string>()
    const ids = new Set<string>()
    for (const row of emailJunctionRows) {
      if (!row.deletedAt) ids.add(row.preOrderId)
    }
    return ids
  }, [emailEnabled, emailJunctionRows])

  // Working set for the in-memory junction filter. Status-filtered when
  // the caller asked for one (every caller today asks for "pending"), so
  // the scan stays small. `proposals[]` is not a Dexie-indexed field, so
  // this can't be pushed down to a Dexie `where`. Gated by `emailEnabled`
  // via the same `__none__` sentinel pattern as the other two queries —
  // otherwise SMS/WhatsApp views would subscribe to every status-matching
  // batch in the company and re-evaluate the junction filter on every
  // batch write for an outcome that's always `[]`.
  const allBatchesForJunction = reactive.useQuery<ProposalBatch>(
    PROPOSAL_BATCH_TABLE,
    emailEnabled
      ? options.status
        ? { status: options.status }
        : undefined
      : { status: "__none__" }
  )
  const byEmailJunction = useMemo(() => {
    if (!emailEnabled || preOrderIdsForEmail.size === 0) return []
    return filterBatchesByPreOrderIds(
      allBatchesForJunction,
      preOrderIdsForEmail
    )
  }, [emailEnabled, preOrderIdsForEmail, allBatchesForJunction])

  return useMemo(() => {
    if (!enabled) return []
    const seen = new Set<string>()
    const merged: ProposalBatch[] = []
    for (const batch of [
      ...byParticipant,
      ...byLegacySource,
      ...byEmailJunction,
    ]) {
      if (seen.has(batch.id)) continue
      if (batch.sourceChannelType !== channelType) continue
      if (options.status && batch.status !== options.status) continue
      seen.add(batch.id)
      merged.push(batch)
    }
    // `?? "0000"` keeps batches with a missing/null createdAt at the END of
    // the desc-sorted list (Dexie's native `orderBy` treats undefined index
    // values as last; we mirror that here for the merged result).
    merged.sort((a, b) =>
      (b.createdAt ?? "0000").localeCompare(a.createdAt ?? "0000")
    )
    return merged
  }, [
    enabled,
    channelType,
    options.status,
    byParticipant,
    byLegacySource,
    byEmailJunction,
  ])
}
